import usePopularBrands from '@/hooks/usePopularBrands';
import FilterSection from '@/components/skus/FilterSkus/FilterSection';
import FilterItem from '@/components/skus/FilterSkus/FilterItem';
import SpiritoryInput from '@/components/common/Forms/SpiritoryInput';
import DefaultButton from '@/components/common/default/DefaultButton';
import { debounce } from '@/utilites/debounce';
import { AlertCircle, ChevronDown, Loader2, Minus, Search, X, Building2 } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRefinementList } from 'react-instantsearch';
import router from 'next/router';
import { createBrandUrl } from '@/utilites/search/create-search-urls';
import { usePathname, useSearchParams } from 'next/navigation';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';

interface SearchableRefinementBrandListFilterProps {
  isOpen: boolean;
  onToggle: () => void;
  limit?: number;
  showMoreLimit?: number;
  icon?: React.ReactNode;
  className?: string;
  disableRedirect?: boolean;
  secondaryAccentColor?: string;
}

// Enhanced constants for better maintainability
const DEFAULT_FILTER_LIMITS = {
  INITIAL: 8,
  EXPANDED: 20,
  SEARCH_THRESHOLD: 2,
  DEBOUNCE_DELAY: 300,
} as const;

const SearchableRefinementBrandListFilter = memo<SearchableRefinementBrandListFilterProps>(
  ({
    isOpen,
    onToggle,
    limit = DEFAULT_FILTER_LIMITS.INITIAL,
    showMoreLimit = DEFAULT_FILTER_LIMITS.EXPANDED,
    icon,
    className = '',
    disableRedirect = false,
    secondaryAccentColor,
  }) => {
    const popularBrands = usePopularBrands();
    const { translate } = useTypedTranslation<CommonEnum>('common');
    const { items, refine, canRefine, isFromSearch, searchForItems } = useRefinementList({
      attribute: 'brand.name',
      limit: showMoreLimit,
    });
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [searchQuery, setSearchQuery] = useState('');
    const [showMore, setShowMore] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Enhanced debounced search with loading state
    const debouncedSearch = useMemo(
      () =>
        debounce((query: string) => {
          setIsSearching(true);
          try {
            searchForItems(query);
          } catch (error) {
            console.error('Search error:', error);
          } finally {
            setTimeout(() => setIsSearching(false), 100);
          }
        }, DEFAULT_FILTER_LIMITS.DEBOUNCE_DELAY),
      [searchForItems]
    );

    const handleSearch = useCallback(
      (value: string) => {
        setSearchQuery(value);
        debouncedSearch(value);
      },
      [debouncedSearch]
    );

    const handleRefine = useCallback(
      (value: string) => {
        try {
          refine(value);
        } catch (error) {
          console.error('Refine error:', error);
        }
      },
      [refine]
    );

    const handleShowMore = useCallback(() => {
      setShowMore(prev => !prev);
    }, []);

    const handleClearSearch = useCallback(() => {
      setSearchQuery('');
      searchForItems('');
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [searchForItems]);

    // Enhanced memoized computations
    const hasActiveRefinements = useMemo(() => items.some(item => item.isRefined), [items]);

    const displayItems = useMemo(() => {
      const filteredItems = searchQuery
        ? items.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()))
        : items;

      // Get popular brand names for quick lookup
      const popularBrandNames = new Set(popularBrands.map(brand => brand.name));

      // Separate popular brands and other brands from filteredItems
      const popularItems: typeof filteredItems = [];
      const otherItems: typeof filteredItems = [];

      filteredItems.forEach(item => {
        if (popularBrandNames.has(item.label)) {
          popularItems.push(item);
        } else {
          otherItems.push(item);
        }
      });

      // Sort popular items by their order in popularBrands array
      popularItems.sort((a, b) => {
        const aIndex = popularBrands.findIndex(brand => brand.name === a.label);
        const bIndex = popularBrands.findIndex(brand => brand.name === b.label);
        return aIndex - bIndex;
      });

      // Sort other items alphabetically (A-Z)
      otherItems.sort((a, b) => a.label.localeCompare(b.label));

      // Combine: popular brands first, then alphabetically sorted brands
      const sortedItems = [...popularItems, ...otherItems];

      return showMore ? sortedItems : sortedItems.slice(0, limit);
    }, [items, searchQuery, showMore, limit, popularBrands]);

    const memoizedItems = useMemo(
      () =>
        displayItems.map(item => (
          <FilterItem
            key={item.value}
            label={item.label}
            count={item.count}
            isSelected={item.isRefined}
            onClick={() => {
              // Skip redirects if disableRedirect is true
              if (disableRedirect) {
                return handleRefine(item.value);
              }

              if (pathname.includes('/brands/') === false) {
                return router.push(createBrandUrl(item.value, searchParams));
              }

              return handleRefine(item.value);
            }}
            disabled={!canRefine}
            aria-label={`${item.isRefined ? translate(CommonEnum.remove_labelVariableName_filter_parenthesis_itemCountVariable_items_parenthesis).replace('{{labelVariableName}}', item.label).replace('{{itemCountVariable}}', item.count.toString()) : translate(CommonEnum.add_labelVariableName_filter_parenthesis_itemCountVariable_items_parenthesis).replace('{{labelVariableName}}', item.label).replace('{{itemCountVariable}}', item.count.toString())}`}
          />
        )),
      [displayItems, handleRefine, canRefine, disableRedirect, pathname, searchParams]
    );

    const showMoreControls = useMemo(() => {
      const canShowMore = !searchQuery && items.length > limit && !showMore;
      const canShowLess = !searchQuery && showMore;

      return { canShowMore, canShowLess };
    }, [searchQuery, items.length, limit, showMore]);

    // Focus management
    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        const timer = setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [isOpen]);

    // Cancel debouncedSearch on unmount
    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    return (
      <FilterSection
        id="brand"
        title={translate(CommonEnum.brand) || 'Brand'}
        icon={icon || <Building2 className="tw-h-5 tw-w-5 tw-text-slate-600" />}
        isOpen={isOpen}
        onToggle={onToggle}
        hasActiveFilters={hasActiveRefinements}
        isLoading={isSearching}
        className={className}
        secondaryAccentColor={secondaryAccentColor}
      >
        <div className="tw-space-y-2">
          <div className="tw-relative tw-px-3">
            <SpiritoryInput
              ref={searchInputRef}
              type="search"
              placeholder={translate(CommonEnum.search_brands) || 'Search brands...'}
              className="tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-py-2.5 tw-pl-10 tw-pr-10 tw-text-sm tw-text-gray-900 tw-placeholder-gray-500 tw-transition-all tw-duration-200 focus:tw-border-gray-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-gray-400"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              disabled={!canRefine}
              aria-label={`${translate(CommonEnum.search)} brands`}
              aria-describedby="brand-search-help"
              rightIcon={
                searchQuery && (
                  <DefaultButton
                    type="button"
                    onClick={handleClearSearch}
                    className="tw-h-4 tw-w-4 tw-transform tw-text-gray-400 tw-transition-colors hover:tw-text-gray-600 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-ring-offset-2"
                    aria-label="Clear search"
                  >
                    <X className="tw-h-4 tw-w-4" />
                  </DefaultButton>
                )
              }
              leftIcon={<Search className="tw-h-4 tw-w-4" />}
            />
            {isSearching && (
              <Loader2 className="tw-absolute tw-right-3 tw-top-1/2 tw-h-4 tw-w-4 tw--translate-y-1/2 tw-animate-spin tw-text-gray-400" />
            )}
          </div>
          <div id="brand-search-help" className="tw-sr-only">
            {translate(CommonEnum.type_to_search_through_titleVariable_options).replace(
              '{{title}}',
              'brand'
            )}
          </div>

          <div
            className="tw-scrollbar-thin tw-scrollbar-track-gray-100 tw-scrollbar-thumb-gray-300 tw-max-h-48 tw-space-y-0.5 tw-overflow-y-auto tw-px-3"
            role="listbox"
            aria-label={`Brand ${translate(CommonEnum.options) || 'options'}`}
          >
            {memoizedItems.length > 0 ? (
              memoizedItems
            ) : (
              <div className="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-text-sm tw-text-gray-500">
                <AlertCircle className="tw-h-4 tw-w-4" />
                <span>
                  {searchQuery
                    ? translate(CommonEnum.no_results_found_for_search_query).replace(
                        '{{searchQuery}}',
                        searchQuery
                      )
                    : translate(CommonEnum.no_options_available) || 'No options available'}
                </span>
              </div>
            )}
          </div>

          {(showMoreControls.canShowMore || showMoreControls.canShowLess) && (
            <div className="tw-border-t tw-border-gray-100 tw-pt-2">
              <DefaultButton
                type="button"
                onClick={handleShowMore}
                className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-ring-offset-2"
                aria-label={
                  showMore
                    ? translate(CommonEnum.show_fewer_options) || 'Show fewer options'
                    : translate(CommonEnum.show_more_options) || 'Show more options'
                }
              >
                {showMore ? (
                  <>
                    <Minus className="tw-h-4 tw-w-4" />
                    {translate(CommonEnum.show_less) || 'Show Less'}
                  </>
                ) : (
                  <>
                    <ChevronDown className="tw-h-4 tw-w-4" />
                    {translate(CommonEnum.show_more) || 'Show More'} ({items.length - limit} more)
                  </>
                )}
              </DefaultButton>
            </div>
          )}
        </div>
      </FilterSection>
    );
  }
);

SearchableRefinementBrandListFilter.displayName = 'SearchableRefinementBrandListFilter';

export default SearchableRefinementBrandListFilter;
